import React, { useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
  Divider,
} from "@pushpress/shared-components";
import { useTaxesQuery } from "../../../../apis/clientApi.hooks";
import { Info } from "lucide-react";
import DisplayGif from "../../../common/DisplayGif/DisplayGif";

interface Value {
  uuid: string;
  name: string;
}

type TaxSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends Value,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function TaxSelect<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends Value,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: TaxSelectProps<TFieldValues, TName, Option, IsMulti, Group> &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const { ...other } = props;

  const { data } = useTaxesQuery({
    staleTime: Infinity,
  });

  const getOptionLabel = (option: Value) => {
    return option.name;
  };

  const getOptionValue = (option: Value) => {
    return option.uuid;
  };

  const options = useMemo(() => {
    return (data?.data || []) as any;
  }, [data]);

  const menuAdornment = (
    <>
      <Divider />
      <div className="tw-flex tw-items-center tw-gap-2 tw-p-2">
        <DisplayGif gif="taxSettings" size={500}>
          <Info className="tw-w-4 tw-h-4 tw-text-muted-foreground" />
        </DisplayGif>

        <span className="tw-text-sm tw-text-muted-foreground">
          Navigate to{" "}
          <a
            href="/settings/index"
            rel="noopener noreferrer"
            target="_blank"
            className="tw-text-link hover:tw-text-link-hover"
          >
            settings
          </a>{" "}
          to add your tax rate.
        </span>
      </div>
    </>
  );

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...other}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      menuAdornment={menuAdornment}
    />
  );
}

export default TaxSelect;
